import { Bugfender, LogLevel } from '@bugfender/sdk';
import { useEffect, useRef } from 'react';
import { Control, FieldValues, useFormState } from 'react-hook-form';

import { BugfenderTagType } from '@/types/document';

interface FormErrorLoggerProps<T extends FieldValues> {
  formName: string;
  control: Control<T, unknown, unknown>;
}

/**
 * Renderless component that subscribes to form errors via useFormState
 * and logs them to Bugfender. By isolating the errors subscription here,
 * the parent component does not re-render due to error logging.
 */
export function FormErrorLogger<T extends FieldValues>({
  formName,
  control,
}: FormErrorLoggerProps<T>) {
  const { errors } = useFormState({ control });
  const prevErrorsRef = useRef<string>('');

  useEffect(() => {
    const serialized = JSON.stringify(errors);
    if (serialized !== prevErrorsRef.current && serialized !== '{}') {
      prevErrorsRef.current = serialized;
      Bugfender.sendLog({
        tag: BugfenderTagType.MORE_INFO,
        level: LogLevel.Warning,
        text: `${formName} - errors: ${JSON.stringify(errors)}`,
      });
    }
  }, [errors, formName]);

  return null;
}
